import { ChevronDownIcon, Loader2, ShieldPlusIcon, StethoscopeIcon } from 'lucide-react'
import { type FocusEvent as ReactFocusEvent, type ReactNode, useRef, useState } from 'react'
import { Form, href, useNavigate, useNavigation, useParams } from 'react-router'
import { twMerge } from 'tailwind-merge'
import { formatIntlSlug, formatLocationSlug, formatZipSlug } from '../slug'
import { SPECIALTIES_BY_LABEL } from '../specialties'
import { isUsRegionCode } from '../us-regions'
import { useGeo } from '../use-geo'
import { InsuranceCombobox, type SelectedInsurance } from './insurance-combobox'

const FOCUS_RING = 'outline-solid outline-2 outline-offset-2 outline-primary-500'

type FocusTrackingProps = {
  onPointerDown: () => void
  onFocus: () => void
  onBlur: (event: ReactFocusEvent<HTMLElement>) => void
}

/**
 * Ring on keyboard focus only (VIT-17930). `:focus-visible` can't express this
 * for either of our controls: Chromium matches it on a `<select>` even for mouse
 * clicks, and a text input matches it whenever focused, click included. So track
 * pointer-before-focus instead — Tab (no pointerdown) rings, click/tap doesn't.
 *
 * Both cells share this so their focus behaviour can't drift apart.
 *
 * The pointer flag is cleared on blur so the next entry is judged fresh —
 * without that, clicking the cell and later tabbing back into it would stay
 * silent. But ONLY when focus actually leaves: these props are attached to a
 * whole row for the insurance cell, whose input belongs to the combobox, and one
 * click there moves focus *within* the row (input → option button → back to the
 * input, which handleSelect refocuses). Resetting mid-gesture made those later
 * focuses read as keyboard and lit the ring on every mouse pick. For a cell that
 * owns its control directly — the specialty <select> — relatedTarget is never
 * inside it, so this is exactly the plain reset.
 */
const useKeyboardFocus = (): { keyboardFocused: boolean; focusProps: FocusTrackingProps } => {
  const [keyboardFocused, setKeyboardFocused] = useState(false)
  const pointerFocusRef = useRef(false)

  return {
    keyboardFocused,
    focusProps: {
      onPointerDown: () => {
        pointerFocusRef.current = true
      },
      onFocus: () => {
        setKeyboardFocused(!pointerFocusRef.current)
      },
      onBlur: (event) => {
        setKeyboardFocused(false)
        // `relatedTarget` is the element gaining focus, and is null when focus
        // leaves the document entirely — `contains(null)` is false, so that
        // counts as leaving.
        if (!event.currentTarget.contains(event.relatedTarget)) {
          pointerFocusRef.current = false
        }
      },
    },
  }
}

// Shared chrome for one search cell: the icon tile, and a column for whatever
// control the cell holds. Presentational only — the two cells hold very
// different controls (an overlaid native <select> vs a typeahead), so this
// composes rather than configures.
//
// The ring is painted on the row rather than the control: the specialty cell's
// real <select> is `opacity-0`, so its own ring would be invisible, and the
// insurance input suppresses its own so the two cells look alike.
const SearchField = ({
  icon,
  children,
  keyboardFocused,
  focusProps,
}: {
  icon: ReactNode
  children: ReactNode
  keyboardFocused: boolean
  /** Attach the tracking to the row instead of the control, for a cell whose
   *  focusable element it doesn't own — the insurance input lives inside the
   *  combobox. React's onFocus/onBlur are focusin/focusout, so they bubble to
   *  the row; onPointerDown bubbles natively. */
  focusProps?: FocusTrackingProps | undefined
}) => (
  <div
    {...focusProps}
    className={twMerge('relative grid grid-cols-[auto_1fr] gap-3 min-w-0 rounded-2xl', keyboardFocused && FOCUS_RING)}
  >
    <div className="rounded-2xl bg-primary-50 size-11 grid place-items-center">{icon}</div>
    <div className="flex flex-col justify-between min-w-0">{children}</div>
  </div>
)

// Label class shared by both cells so the typeahead's own <label> matches the
// select's.
const FIELD_LABEL_CLASS = 'tracking-wide block text-xs uppercase font-bold text-secondary-600'

// The picker names a group of providers, same as the results heading and the
// browse-by-city links, so it reads plural (VIT-17926). Mapped here rather than
// teaching SelectField about a second label field — the insurance list has one.
const SPECIALTY_OPTIONS = SPECIALTIES_BY_LABEL.map(({ slug, pluralLabel }) => ({ slug, label: pluralLabel }))

// One search field backed by a native <select>, stretched invisibly across the
// whole row so a click (or tap) anywhere — not just on the value — opens it.
// Because the real select is transparent, React paints the selected label itself
// from `value`.
const SelectField = ({
  id,
  label,
  icon,
  options,
  defaultValue,
  placeholder,
}: {
  id: string
  label: string
  icon: ReactNode
  options: readonly { slug: string; label: string }[]
  defaultValue: string
  /** Shown for the empty option, and as the unselected field value. */
  placeholder: string
}) => {
  const [value, setValue] = useState(defaultValue)
  const { keyboardFocused, focusProps } = useKeyboardFocus()
  const selected = options.find((option) => option.slug === value)

  return (
    <SearchField icon={icon} keyboardFocused={keyboardFocused}>
      <label htmlFor={id} className={FIELD_LABEL_CLASS}>
        {label}
      </label>
      {/* `pr-6 truncate`: the chevron is positioned over the right edge, so
          reserve space for it and ellipsise the longest labels ("Primary
          Care Providers") at narrow widths. Unselected, this paints the
          placeholder in the same weight and colour the insurance input's real
          ::placeholder uses, so the two empty cells look identical. */}
      <div
        className={twMerge(
          'min-w-0 truncate pr-6 text-base font-bold',
          // Matches INSURANCE_INPUT_CLASS's ::placeholder — see the contrast note there.
          selected == null && 'font-normal text-secondary-500',
        )}
      >
        {selected?.label ?? placeholder}
      </div>
      {/* Invisible but real: fills the whole row, still owns focus, the
          keyboard, and form submission. Clicks anywhere in the row land here. */}
      <select
        className="absolute inset-0 size-full cursor-pointer appearance-none opacity-0"
        id={id}
        name={id}
        data-testid={`${id}-select`}
        value={value}
        // `required` because the empty option can't be searched: the route is
        // /us/:location/:specialty and the API keys on a NUCC code, so there is
        // no "any specialty" query to run. Native validation blocks the submit
        // before the hand-rolled onSubmit sees it, the same way the insurance
        // field's setCustomValidity does.
        required
        {...focusProps}
        onChange={(event) => setValue(event.target.value)}
      >
        {/* `disabled` so it can be the initial value but never chosen again —
            there is nothing to search once a specialty has been picked, and
            re-selecting it would only strand the visitor on a blocked submit. */}
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.slug} value={option.slug}>
            {option.label}
          </option>
        ))}
      </select>
      {/* `pointer-events-none` so a click on the chevron passes through to the
          select underneath rather than being swallowed by the SVG. */}
      <ChevronDownIcon className="size-4 text-secondary-700 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
    </SearchField>
  )
}

// Strips the combobox's standalone-input chrome so it reads like the specialty
// cell's painted value: borderless, transparent, bold. twMerge in the combobox
// makes these win over its base utilities rather than fight on specificity. The
// row already draws the keyboard focus ring, so the input suppresses its own.
// secondary-500, not 400: the placeholder is 16px at normal weight, so WCAG AA
// wants 4.5:1 against the white field. secondary-400 measures 2.63:1 and fails;
// secondary-500 is 4.76:1 and still reads as clearly lighter than the bold value.
const INSURANCE_INPUT_CLASS =
  'border-0 bg-transparent rounded-none p-0 text-base font-bold text-secondary-900 focus-visible:outline-none placeholder:font-normal placeholder:text-secondary-500'

export const SearchForm = ({
  simple = false,
  insurance = null,
}: {
  simple?: boolean
  /** The plan `?insurance=` names, already resolved by the route loader. Home
   *  passes nothing — deliberately: `/` is edge-cached with the query string OUT
   *  of the cache key, so nothing that page renders may derive from it. */
  insurance?: SelectedInsurance | null
}) => {
  const { geo, viewerGeo, picked } = useGeo()

  const navigate = useNavigate()
  const navigation = useNavigation()
  const params = useParams()

  const [selectedInsurance, setSelectedInsurance] = useState<SelectedInsurance | null>(insurance)
  // Re-seed when the route resolves a different plan than the one held locally:
  // back/forward between a filtered and unfiltered results URL re-runs the loader
  // but leaves this component mounted, so the field would otherwise name a filter
  // the results no longer have. React's render-phase prop-change pattern,
  // preferred over key-ing the form — that would remount a node carrying
  // `view-transition-name: search-form` mid-transition.
  const [seededInsuranceId, setSeededInsuranceId] = useState(insurance?.id ?? null)
  if ((insurance?.id ?? null) !== seededInsuranceId) {
    setSeededInsuranceId(insurance?.id ?? null)
    setSelectedInsurance(insurance)
  }

  // `viewerGeo`, NOT `geo`: the catalog documents `state` as the *patient's*
  // state, and `geo` follows the picked location once the visitor has used the
  // picker. Keying on the searched region would filter the visitor's own
  // state-scoped plan out of the suggestions the moment they searched another
  // state — an Oregon patient looking for a Texas doctor still carries an Oregon
  // plan.
  const region = viewerGeo?.region?.trim().toUpperCase() ?? ''
  const insuranceState = isUsRegionCode(region) ? region : undefined

  // Tracked on the row rather than the input: the input belongs to the combobox.
  const insuranceFocus = useKeyboardFocus()

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        const formData = new FormData(e.currentTarget)

        const specialty = formData.get('specialty') as string
        const insuranceId = String(formData.get('insurance') ?? '').trim()

        const city = String(formData.get('city') ?? '')
        const region = String(formData.get('region') ?? '')
        const country = String(formData.get('country') ?? '').trim()
        const pickedPostalCode = String(formData.get('pickedPostalCode') ?? '')

        // Inside the US the slug carries the state; outside it the `intl-` form
        // carries the country, so a non-US results heading reads "Auckland, NZ"
        // rather than echoing an ISO 3166-2 subdivision code no viewer
        // recognises. The two forms are kept apart rather than sharing the region
        // slot because 27 country codes collide with USPS state codes — see
        // INTL_SLUG_RE in slug.ts.
        const inUs = isUsRegionCode(region)

        // Outside the US the country is the qualifier. `US` is deliberately not
        // one: a US viewer whose state didn't resolve has nothing searchable (the
        // providers API keys on city + state), so that submit stays a no-op rather
        // than inventing "intl-houston--us".
        const qualifier = inUs ? region : country.toUpperCase() === 'US' ? '' : country

        // A picked ZIP's city/state now wins (VIT-17833): geo already reflects the
        // pick via pickedToGeo, so the city--region slug names the place the
        // visitor chose and the search gains the state that ZIP-only searches
        // skip. The ZIP is only the fallback — some HUD ZIPs carry no city/state
        // at all.
        //
        // Geo otherwise resolves client-side after mount, so a submit can race it
        // (or geo may never resolve at all). With neither a location pair nor a
        // picked ZIP the slug would be malformed ("--" / "houston--") and the
        // results loader would 404 — stay on the page instead of navigating to a
        // guaranteed error.
        const location =
          city !== '' && qualifier !== ''
            ? inUs
              ? formatLocationSlug(city, qualifier)
              : formatIntlSlug(city, qualifier)
            : pickedPostalCode !== ''
              ? formatZipSlug(pickedPostalCode)
              : null

        if (location === null) return

        const pathname = href('/us/:location/:specialty', {
          location,
          specialty,
        })

        // An empty field is "no insurance filter" — keep the results URL clean.
        const search = insuranceId !== '' ? `?insurance=${encodeURIComponent(insuranceId)}` : ''

        navigate(`${pathname}${search}`, { viewTransition: true })
      }}
      className="[view-transition-name:search-form] search-form space-y-4 mx-auto max-w-7xl text-secondary-900"
      viewTransition
    >
      <div
        className={`grid grid-cols-1 border border-secondary-100 sm:grid-cols-[1fr_1fr_auto] items-center shadow-xl shadow-secondary-800/10 px-4 ${simple ? 'py-2' : 'py-4'} gap-4 rounded-3xl sm:rounded-full bg-white`}
      >
        <SelectField
          id="specialty"
          label="Specialty"
          icon={<StethoscopeIcon className="size-5 text-primary-500 stroke-2" />}
          options={SPECIALTY_OPTIONS}
          // Empty on the home page so both cells start unfilled; the results
          // page seeds it from the path.
          defaultValue={params['specialty'] ?? ''}
          placeholder="Select a specialty"
        />

        {/* No parent <label htmlFor>: the combobox owns its own, so adding one
            here would give the input two labels and leave a dangling htmlFor.
            No chevron either — this isn't click-to-open, and a chevron promises
            a <select>. */}
        <SearchField
          icon={<ShieldPlusIcon className="size-5 text-primary-500 stroke-2" />}
          keyboardFocused={insuranceFocus.keyboardFocused}
          focusProps={insuranceFocus.focusProps}
        >
          <InsuranceCombobox
            name="insurance"
            label="Insurance"
            // `grow` so the combobox fills the cell rather than sitting at its
            // natural height, and `justify-between` so it spreads its own label
            // and field the way the specialty cell spreads its label and painted
            // value. Without both, the two cells' value lines don't share a
            // baseline. (The popover is a third child but never in flow — it is
            // display:none when closed and in the top layer when open.)
            className="grow justify-between"
            labelClassName={FIELD_LABEL_CLASS}
            inputClassName={INSURANCE_INPUT_CLASS}
            placeholder="Any insurance"
            value={selectedInsurance}
            onChange={setSelectedInsurance}
            state={insuranceState}
            data-testid="insurance-combobox"
          />
        </SearchField>

        {/* Controlled (`value`, not `defaultValue`): geo arrives after mount,
            and React ignores defaultValue changes once an input is mounted —
            with defaultValue these would submit empty forever. React exempts
            hidden inputs from the controlled-without-onChange warning. */}
        <input value={geo?.city ?? ''} name="city" type="hidden" />
        <input value={geo?.region ?? ''} name="region" type="hidden" />
        <input value={geo?.postalCode ?? ''} name="postalCode" type="hidden" />
        <input value={geo?.country ?? ''} name="country" type="hidden" />
        {/* The ZIP from an explicit pick in the location picker (VIT-17740),
            kept separate from `postalCode` above so the submit handler can tell
            "the visitor chose this" from CloudFront's IP-derived guess. Now only
            the fallback for a ZIP the dataset has no city/state for — otherwise
            the pick's city/state reach the handler through the inputs above
            (VIT-17833). */}
        <input value={picked?.postalCode ?? ''} name="pickedPostalCode" type="hidden" />

        <button
          type="submit"
          // In `simple` mode the visible "Search" text is hidden from sm up,
          // leaving an icon-only button — the SVG is aria-hidden, so without
          // this the button has no accessible name at all.
          aria-label="Search"
          className={`sm:ml-auto btn btn-primary btn-lg py-3 ${simple ? 'sm:size-12 sm:py-0' : 'sm:px-6 sm:py-4'}`}
          data-testid="search-submit"
        >
          {navigation.state === 'loading' ? (
            <Loader2 className="size-6 animate-spin shrink-0" />
          ) : (
            <svg
              aria-hidden="true"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="size-6 shrink-0"
            >
              <path
                d="M10 6C10 5.44772 10.4477 5 11 5C14.3137 5 17 7.68629 17 11C17 11.5523 16.5523 12 16 12C15.4477 12 15 11.5523 15 11C15 8.79086 13.2091 7 11 7C10.4477 7 10 6.55228 10 6Z"
                fill="#FFF0E9"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11C20 13.125 19.2635 15.078 18.0319 16.6177L21.7071 20.2929C22.0976 20.6834 22.0976 21.3166 21.7071 21.7071C21.3166 22.0976 20.6834 22.0976 20.2929 21.7071L16.6177 18.0319C15.078 19.2635 13.125 20 11 20C6.02944 20 2 15.9706 2 11ZM11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4Z"
                fill="#FFF0E9"
              />
            </svg>
          )}
          <span className={`${simple ? 'sm:hidden' : ''}`}>Search</span>
        </button>
      </div>
    </Form>
  )
}
